import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Mail, Send } from "lucide-react";
import api, { apiErr } from "@/lib/api";

export function EmailSendDialog({ invoiceId, docType = "invoice", number, onClose }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${invoiceId}/email/preview`, { params: { doc_type: docType } });
        if (!active) return;
        setSubject(data.subject || "");
        setBody(data.body || "");
        setTo(data.to || "");
        setCc(data.cc || null);
      } catch (e) {
        toast.error(apiErr(e));
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [invoiceId, docType, onClose]);

  const send = async () => {
    if (!to) return toast.error("Keine Kunden-E-Mail hinterlegt. Bitte in der Rechnung ergänzen.");
    if (!subject.trim()) return toast.error("Bitte einen Betreff eingeben.");
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/email`, { doc_type: docType, subject, body });
      toast.success(`Per E-Mail an ${data.to} gesendet${data.cc ? ` (Kopie an ${data.cc})` : ""}`);
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="no-print fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm px-4" data-testid="email-dialog">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-display font-semibold text-base inline-flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-600" /> {docType === "reminder" ? "Mahnung" : "Rechnung"} {number} per E-Mail
          </h3>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100" data-testid="email-dialog-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Lädt…</div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="text-xs text-slate-500">
              An: <span className="font-medium text-slate-700" data-testid="email-dialog-to">{to || "—"}</span>
              {cc && <> · Kopie an: <span className="font-medium text-slate-700">{cc}</span></>}
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-slate-600 mb-1">Betreff</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-dialog-subject" className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-600 mb-1">Anschreiben</span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} data-testid="email-dialog-body" className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
              <span className="block text-xs text-slate-400 mt-1">Anrede und Grußformel werden automatisch ergänzt.</span>
            </label>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/60">
          <button onClick={onClose} disabled={sending} className="text-sm px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60" data-testid="email-dialog-cancel">Abbrechen</button>
          <button onClick={send} disabled={loading || sending} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-60" data-testid="email-dialog-send">
            <Send className="h-4 w-4" /> {sending ? "Sendet…" : "Senden"}
          </button>
        </div>
      </div>
    </div>
  );
}
